import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, ChevronLeft, Clock, X } from "lucide-react";
import {
  finishAttempt,
  startAttempt,
  submitAnswer,
  type AttemptResult,
  type AttemptStartOut,
  type QuestionTake,
} from "../api/quizzes";
import { ApiError } from "../api/client";

type AnswerValue = { selected_option_ids?: number[]; text_answer?: string };
type Answers = Record<number, AnswerValue>;

function isAnswered(a: AnswerValue | undefined): boolean {
  if (!a) return false;
  if (a.text_answer && a.text_answer.trim().length > 0) return true;
  if (a.selected_option_ids && a.selected_option_ids.length > 0) return true;
  return false;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function StudentQuizPage() {
  const { quizId } = useParams<{ quizId: string }>();
  const id = Number(quizId);
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState<AttemptStartOut | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [current, setCurrent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const start = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError("Некорректный тест");
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const a = await startAttempt(id);
      setAttempt(a);
      setAnswers({});
      setCurrent(0);
      setResult(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409)
        setError("Попытки исчерпаны. Обратитесь к куратору.");
      else if (err instanceof ApiError && err.status === 404)
        setError("Тест не найден.");
      else
        setError(
          err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна",
        );
    } finally {
      setStarting(false);
    }
  }, [id]);

  useEffect(() => {
    void start();
  }, [start]);

  const handleSubmit = useCallback(async () => {
    if (!attempt) return;
    setSubmitting(true);
    try {
      for (const q of attempt.questions) {
        const a = answers[q.id];
        if (!a) continue;
        await submitAnswer(attempt.attempt_id, {
          question_id: q.id,
          selected_option_ids: a.selected_option_ids,
          text_answer: a.text_answer,
        });
      }
      const r = await finishAttempt(attempt.attempt_id);
      setResult(r);
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setSubmitting(false);
    }
  }, [attempt, answers]);

  // Timer
  const startedAtMs = attempt
    ? new Date(attempt.started_at).getTime()
    : null;
  const totalSeconds =
    attempt?.time_limit_minutes != null
      ? attempt.time_limit_minutes * 60
      : null;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (totalSeconds == null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [totalSeconds]);
  const remainingSeconds = useMemo(() => {
    if (totalSeconds == null || startedAtMs == null) return null;
    const elapsed = Math.floor((now - startedAtMs) / 1000);
    return Math.max(0, totalSeconds - elapsed);
  }, [totalSeconds, startedAtMs, now]);

  // Auto-submit on timer expiry
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (
      remainingSeconds === 0 &&
      attempt &&
      !result &&
      !autoSubmittedRef.current
    ) {
      autoSubmittedRef.current = true;
      void handleSubmit();
    }
  }, [remainingSeconds, attempt, result, handleSubmit]);

  function setSingleAnswer(qid: number, optionId: number) {
    setAnswers((p) => ({ ...p, [qid]: { selected_option_ids: [optionId] } }));
  }
  function toggleMultiAnswer(qid: number, optionId: number) {
    setAnswers((p) => {
      const cur = p[qid]?.selected_option_ids ?? [];
      const next = cur.includes(optionId)
        ? cur.filter((x) => x !== optionId)
        : [...cur, optionId];
      return { ...p, [qid]: { selected_option_ids: next } };
    });
  }
  function setTextAnswer(qid: number, value: string) {
    setAnswers((p) => ({ ...p, [qid]: { text_answer: value } }));
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <p className="text-ink">{error}</p>
        <Link to="/" className="mt-4 inline-block text-sm text-brand hover:underline">
          ← на главную
        </Link>
      </div>
    );
  }

  if (starting || !attempt) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center text-ink-muted">
        Загружаем тест…
      </div>
    );
  }

  if (result) {
    return (
      <ResultView
        result={result}
        attempt={attempt}
        onRetry={start}
        onContinue={() => navigate(-1)}
      />
    );
  }

  const total = attempt.questions.length;
  const q = attempt.questions[current];
  if (!q) {
    return null;
  }
  const isLast = current === total - 1;
  const currentAnswered = isAnswered(answers[q.id]);
  const lowTime = remainingSeconds != null && remainingSeconds < 60;

  return (
    <div className="min-h-full bg-surface pb-32">
      {/* Top bar */}
      <header className="sticky top-0 z-10 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center px-4 py-3">
          <div className="flex-1 text-sm font-medium text-ink-soft">
            Вопрос <span className="font-semibold tabular-nums text-ink">{current + 1}</span>{" "}
            из <span className="tabular-nums">{total}</span>
          </div>
          {remainingSeconds != null && (
            <div
              className={`mr-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums ${
                lowTime
                  ? "bg-brand-danger/10 text-brand-danger"
                  : "bg-surface-alt text-ink-soft"
              }`}
            >
              <Clock size={12} strokeWidth={2.25} />
              {formatTime(remainingSeconds)}
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              if (confirm("Прервать тест? Ответы не сохранятся.")) {
                navigate(-1);
              }
            }}
            className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
            aria-label="Закрыть"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 pt-6">
        <h2 className="text-xl font-semibold leading-snug text-ink">
          {q.question_text}
        </h2>

        <div className="mt-5">
          <QuestionInput
            question={q}
            answer={answers[q.id]}
            onSingle={(oid) => setSingleAnswer(q.id, oid)}
            onMulti={(oid) => toggleMultiAnswer(q.id, oid)}
            onText={(v) => setTextAnswer(q.id, v)}
          />
        </div>
      </main>

      {/* Bottom: dots + nav */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-line bg-white/95 backdrop-blur">
        <div className="mx-auto max-w-2xl px-4 pt-3 pb-3">
          {/* Dots */}
          <div className="mb-3 flex flex-wrap items-center justify-center gap-1.5">
            {attempt.questions.map((qq, idx) => {
              const answered = isAnswered(answers[qq.id]);
              const isCurrent = idx === current;
              return (
                <button
                  key={qq.id}
                  type="button"
                  onClick={() => setCurrent(idx)}
                  className={`grid h-7 w-7 place-items-center rounded-full text-[11px] font-semibold tabular-nums transition ${
                    isCurrent
                      ? "bg-brand text-white"
                      : answered
                        ? "bg-brand/15 text-brand"
                        : "bg-surface-alt text-ink-muted hover:bg-line"
                  }`}
                  aria-label={`Вопрос ${idx + 1}`}
                  aria-current={isCurrent ? "page" : undefined}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>

          {/* Nav */}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setCurrent((c) => Math.max(0, c - 1))}
              disabled={current === 0}
              className="inline-flex items-center gap-1.5 px-2 py-2 text-sm font-medium text-ink-soft transition hover:text-ink disabled:cursor-not-allowed disabled:text-ink-muted/50"
            >
              <ChevronLeft size={16} strokeWidth={2.25} />
              Назад
            </button>
            {isLast ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-dark disabled:bg-slate-300"
              >
                {submitting ? "Проверяем…" : "Сдать тест"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setCurrent((c) => Math.min(total - 1, c + 1))}
                disabled={!currentAnswered}
                className="rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                Ответить
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function QuestionInput({
  question,
  answer,
  onSingle,
  onMulti,
  onText,
}: {
  question: QuestionTake;
  answer?: AnswerValue;
  onSingle: (optionId: number) => void;
  onMulti: (optionId: number) => void;
  onText: (value: string) => void;
}) {
  if (question.question_type === "text") {
    return (
      <textarea
        rows={5}
        placeholder="Ваш ответ"
        value={answer?.text_answer ?? ""}
        onChange={(e) => onText(e.target.value)}
        className="input min-h-[120px] resize-y"
      />
    );
  }
  const selected = new Set(answer?.selected_option_ids ?? []);
  const isMulti = question.question_type === "multi_choice";
  return (
    <div className="space-y-2">
      {question.options.map((o) => {
        const isSelected = selected.has(o.id);
        return (
          <label
            key={o.id}
            className={`group flex cursor-pointer items-center gap-3 rounded-xl border-2 bg-white px-4 py-3 text-sm transition ${
              isSelected
                ? "border-brand bg-brand/5"
                : "border-line hover:border-ink-muted/40"
            }`}
          >
            <span
              className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 transition ${
                isSelected
                  ? "border-brand bg-brand text-white"
                  : "border-line bg-white"
              } ${isMulti ? "!rounded-md" : ""}`}
            >
              {isSelected &&
                (isMulti ? (
                  <Check size={12} strokeWidth={3} />
                ) : (
                  <span className="h-2 w-2 rounded-full bg-white" />
                ))}
            </span>
            <input
              type={isMulti ? "checkbox" : "radio"}
              name={`q${question.id}`}
              checked={isSelected}
              onChange={() => (isMulti ? onMulti(o.id) : onSingle(o.id))}
              className="sr-only"
            />
            <span
              className={`flex-1 text-sm ${
                isSelected ? "font-medium text-ink" : "text-ink-soft"
              }`}
            >
              {o.option_text}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function ResultView({
  result,
  attempt,
  onRetry,
  onContinue,
}: {
  result: AttemptResult;
  attempt: AttemptStartOut;
  onRetry: () => void;
  onContinue: () => void;
}) {
  const passed = result.passed;
  const correctCount =
    result.breakdown?.filter((b) => b.is_correct).length ??
    Math.round((result.score_percent / 100) * attempt.questions.length);
  const [showBreakdown, setShowBreakdown] = useState(false);

  return (
    <div className="min-h-full bg-surface pb-12">
      <header className="border-b border-line bg-surface px-4 py-3">
        <div className="mx-auto flex max-w-2xl items-center">
          <h1 className="flex-1 text-center text-base font-semibold text-ink">
            Результат теста
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 pt-8 text-center">
        <div
          className={`mx-auto grid h-20 w-20 place-items-center rounded-full ${
            passed ? "bg-brand-success/15 text-brand-success" : "bg-brand-danger/15 text-brand-danger"
          }`}
          aria-hidden
        >
          {passed ? (
            <Check size={40} strokeWidth={2.5} />
          ) : (
            <X size={40} strokeWidth={2.5} />
          )}
        </div>
        <h2 className="mt-6 text-2xl font-bold text-ink">
          {passed ? "Отлично!" : "Пока не получилось"}
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          {passed ? "Вы успешно прошли тест" : "Этот тест пока не пройден"}
        </p>

        <div
          className={`mx-auto mt-6 rounded-2xl px-6 py-5 ${
            passed ? "bg-brand-success/10" : "bg-brand-danger/10"
          }`}
        >
          <div className="text-xs uppercase tracking-wide text-ink-muted">
            Ваш результат
          </div>
          <div
            className={`mt-1 text-4xl font-bold tabular-nums ${
              passed ? "text-brand-success" : "text-brand-danger"
            }`}
          >
            {result.score_percent}%
          </div>
          <div className="mt-1 text-xs text-ink-muted">
            {correctCount} из {attempt.questions.length} правильных ответов
          </div>
        </div>

        {!passed && (
          <div className="card mt-5 p-4 text-left">
            <div className="text-sm font-semibold text-ink">Что делать дальше?</div>
            <ol className="mt-2 space-y-1 pl-4 text-sm text-ink-soft">
              <li className="list-decimal">Перечитайте материал урока.</li>
              <li className="list-decimal">Посмотрите разбор ошибок ниже.</li>
              <li className="list-decimal">Попробуйте пройти тест ещё раз.</li>
            </ol>
          </div>
        )}

        {passed && (
          <div className="card mt-5 p-4 text-left">
            <div className="text-sm font-semibold text-ink">Рекомендуем</div>
            <p className="mt-1 text-sm text-ink-soft">
              Продолжайте в том же темпе. Вы хорошо усвоили материал.
            </p>
          </div>
        )}

        <div className="mt-7 space-y-3">
          {passed ? (
            <button
              type="button"
              onClick={onContinue}
              className="w-full rounded-lg bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-dark"
            >
              Продолжить обучение
            </button>
          ) : (
            <button
              type="button"
              onClick={onRetry}
              className="w-full rounded-lg bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-dark"
            >
              Повторить тест
            </button>
          )}

          {result.show_breakdown && result.breakdown && (
            <button
              type="button"
              onClick={() => setShowBreakdown((v) => !v)}
              className="w-full text-sm text-brand transition hover:underline"
            >
              {showBreakdown
                ? "Скрыть разбор"
                : passed
                  ? "Посмотреть разбор"
                  : "Разбор ошибок"}
            </button>
          )}
        </div>

        {showBreakdown && result.show_breakdown && result.breakdown && (
          <ol className="mt-6 space-y-3 text-left">
            {result.breakdown.map((b) => {
              const q = attempt.questions.find((qq) => qq.id === b.question_id);
              return (
                <li
                  key={b.question_id}
                  className={`rounded-xl border-l-4 bg-white p-3 ring-1 ring-line ${
                    b.is_correct ? "border-brand-success" : "border-brand-danger"
                  }`}
                >
                  <p className="text-sm font-medium text-ink">
                    {q?.question_text ?? `Вопрос ${b.question_id}`}
                  </p>
                  <p className="mt-1 text-xs text-ink-muted">
                    {b.is_correct ? "Правильно" : "Неправильно"} ·{" "}
                    {b.points_awarded}/{b.max_points} б.
                  </p>
                  {b.correct_answer_text && (
                    <p className="mt-1 text-xs text-ink-soft">
                      Правильный ответ: <strong>{b.correct_answer_text}</strong>
                    </p>
                  )}
                  {b.correct_option_ids && q && (
                    <p className="mt-1 text-xs text-ink-soft">
                      Правильно:{" "}
                      <strong>
                        {q.options
                          .filter((o) => b.correct_option_ids?.includes(o.id))
                          .map((o) => o.option_text)
                          .join(", ")}
                      </strong>
                    </p>
                  )}
                  {b.explanation && (
                    <p className="mt-1 text-xs italic text-ink-muted">
                      {b.explanation}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </main>
    </div>
  );
}
